"use client";

import React, { useState, useEffect, useRef, Fragment } from 'react';
import { X, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';

interface VideoPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    playlist: { url: string; title: string }[];
    initialIndex?: number;
}

export const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
    isOpen,
    onClose,
    playlist,
    initialIndex = 0
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex, isOpen]);

    useEffect(() => {
        if (isOpen && videoRef.current) {
            videoRef.current.play().catch(err => console.error("Auto-play failed:", err));
            setIsPlaying(true);
        }
    }, [currentIndex, isOpen]);

    if (playlist.length === 0) return null;

    const currentVideo = playlist[currentIndex];

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) videoRef.current.pause();
            else videoRef.current.play();
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const duration = videoRef.current.duration;
            setProgress((current / duration) * 100);
        }
    };

    const handleEnded = () => {
        if (currentIndex < playlist.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setIsPlaying(false);
        }
    };

    const nextVideo = () => {
        if (currentIndex < playlist.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const prevVideo = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-200" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/95 backdrop-blur-xl transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform transition-all w-full max-w-5xl">
                                <button
                                    onClick={onClose}
                                    className="absolute -top-12 right-0 p-2 text-zinc-500 hover:text-white hover:bg-white/10 rounded-full transition-all z-10 outline-none"
                                >
                                    <X size={24} />
                                </button>

                                <div className="w-full aspect-video relative group overflow-hidden rounded-2xl border border-white/10 bg-black">
                                    <video
                                        ref={videoRef}
                                        src={currentVideo.url}
                                        className="w-full h-full object-contain"
                                        onTimeUpdate={handleTimeUpdate}
                                        onEnded={handleEnded}
                                        muted={isMuted}
                                        autoPlay
                                    />

                                    {/* Overlays */}
                                    <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/80 via-black/40 to-transparent p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        {/* Header Info */}
                                        <div className="mb-4">
                                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 mb-1 block">
                                                Preview Playback • Segment {currentIndex + 1}/{playlist.length}
                                            </span>
                                            <h3 className="text-xl font-bold text-white tracking-tight">{currentVideo.title}</h3>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="w-full h-1 bg-white/10 rounded-none mb-6 relative cursor-pointer overflow-hidden">
                                            <div
                                                className="absolute top-0 left-0 h-full bg-blue-600 transition-all duration-100"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>

                                        {/* Controls */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-6">
                                                <button onClick={prevVideo} disabled={currentIndex === 0} className="text-white disabled:opacity-30 hover:text-blue-400 transition-colors">
                                                    <SkipBack size={20} fill="currentColor" />
                                                </button>
                                                <button onClick={togglePlay} className="w-12 h-12 bg-white text-black rounded-none flex items-center justify-center transition-transform">
                                                    {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
                                                </button>
                                                <button onClick={nextVideo} disabled={currentIndex === playlist.length - 1} className="text-white disabled:opacity-30 hover:text-blue-400 transition-colors">
                                                    <SkipForward size={20} fill="currentColor" />
                                                </button>
                                            </div>

                                            <div className="flex items-center gap-4">
                                                <button onClick={() => setIsMuted(!isMuted)} className="text-white/70 hover:text-white">
                                                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                                                </button>
                                                <button className="text-white/70 hover:text-white" onClick={() => videoRef.current?.requestFullscreen()}>
                                                    <Maximize2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Playlist Sidebar (Optional/Small) */}
                                <div className="fixed left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-4 custom-scrollbar">
                                    {playlist.map((item, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentIndex(idx)}
                                            className={`w-40 p-3 rounded-none border transition-all text-left group/item ${idx === currentIndex
                                                ? 'bg-blue-600 border-blue-500 text-white'
                                                : 'bg-zinc-900/50 border-white/5 text-zinc-500 hover:border-white/20 hover:bg-zinc-800'
                                                }`}
                                        >
                                            <div className="text-[10px] font-bold opacity-50 mb-1">SEGMENT {idx + 1}</div>
                                            <div className="text-xs font-medium truncate">{item.title}</div>
                                        </button>
                                    ))}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
